import { useMembershipCta } from "@/hooks/useMembershipCta";
import { Link } from "@tanstack/react-router";
import { Instagram, Facebook, Youtube } from "lucide-react";
import { CreateAccountCTA } from "@/components/CreateAccountCTA";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { InstallAppButton } from "@/components/InstallAppButton";
import logoAsset from "@/assets/logo.png.asset.json";
import { openExternal } from "@/lib/open-external";

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.87a8.16 8.16 0 0 0 4.77 1.52V6.94a4.85 4.85 0 0 1-1.84-.25z" />
    </svg>
  );
}

function PinterestIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M12 2C6.48 2 2 6.48 2 12c0 4.24 2.64 7.86 6.36 9.31-.09-.79-.17-2 .04-2.86.19-.78 1.24-4.95 1.24-4.95s-.32-.63-.32-1.57c0-1.47.85-2.57 1.91-2.57.9 0 1.34.68 1.34 1.49 0 .91-.58 2.27-.88 3.53-.25 1.06.53 1.92 1.57 1.92 1.89 0 3.34-1.99 3.34-4.86 0-2.54-1.83-4.32-4.44-4.32-3.03 0-4.81 2.27-4.81 4.61 0 .91.35 1.89.79 2.42.09.1.1.19.07.3-.08.32-.26 1.06-.29 1.21-.05.2-.16.24-.36.14-1.36-.63-2.21-2.62-2.21-4.21 0-3.43 2.49-6.58 7.18-6.58 3.77 0 6.7 2.69 6.7 6.28 0 3.75-2.36 6.76-5.64 6.76-1.1 0-2.14-.57-2.49-1.25l-.68 2.59c-.25.95-.91 2.14-1.36 2.86.99.31 2.05.48 3.15.48 5.52 0 10-4.48 10-10S17.52 2 12 2z" />
    </svg>
  );
}

const EXPLORE_LINKS: Array<{ label: string; to?: string; hash?: string }> = [
  { label: "About", to: "/about" },
  { label: "Find a Therapist", to: "/therapist-directory" },
  { label: "Become a Therapist Member", to: "/therapist-application" },
  { label: "Join Recovery Trauma™", to: "/choose-membership" },
  { label: "Redeem Your Gift 🎁", to: "/redeem-gift" },
  { label: "Explore as Guest", to: "/guest" },
  { label: "FAQ", to: "/faq" },
  { label: "Support", to: "/support" },
  { label: "Contact", to: "/contact" },
]

const LEGAL_LINKS = [
  { label: "Disclaimer", to: "/disclaimer" },
  { label: "AI Disclaimer", to: "/ai-disclaimer" },
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms & Conditions", to: "/terms" },
  { label: "End User Licence (EULA)", to: "/eula" },
  { label: "Cookie Policy", to: "/cookies" },
  { label: "Community Guidelines", to: "/community-guidelines" },
  { label: "Crisis Support", to: "/crisis" },
];

const SOCIALS = [
  { label: "Instagram", href: "https://www.instagram.com/recoverytrauma_", icon: Instagram },
  { label: "Facebook", href: "https://www.facebook.com/people/Recovery-Trauma/61568168060890", icon: Facebook },
  { label: "Pinterest", href: "https://www.pinterest.com/recoverytrauma", icon: PinterestIcon },
  { label: "TikTok", href: "https://www.tiktok.com/@recoverytrauma", icon: TikTokIcon },
  { label: "YouTube", href: "https://www.youtube.com/@RecoveryTrauma", icon: Youtube },
] as const;

const linkClass =
  "inline-block text-[15px] text-foreground/75 transition-colors hover:text-[var(--gold-deep)]";

const headingClass =
  "text-[12px] font-semibold uppercase tracking-[0.22em] text-[var(--gold-deep)] font-display";

export function LandingFooter() {
  const year = new Date().getFullYear();
  const { showJoinCta } = useMembershipCta();

  return (
    <footer className="relative mt-24 bg-gradient-to-b from-card via-[var(--cream,#FBF5E7)]/40 to-[var(--cream,#FBF5E7)]/70">
      {/* Hairline gold divider */}
      <div className="mx-auto h-px max-w-6xl bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

      <div className="mx-auto max-w-6xl px-6 pb-12 pt-14 sm:px-8 sm:pt-16">
        {/* Brand */}
        <div className="mb-12 max-w-md">
          <div className="flex items-center gap-3">
            <img
              src={logoAsset.url}
              alt="Recovery Trauma logo"
              loading="lazy"
              className="h-10 w-10 rounded-full object-contain"
            />
            <p className="font-display text-xl tracking-wide text-foreground">
              Recovery Trauma<span className="text-[var(--gold-deep)]">™</span>
            </p>
          </div>
          <p className="mt-2 ps-[52px] font-display text-sm italic text-foreground/65">
            Healing Beyond Survival Mode<span className="text-[var(--gold-deep)]">™</span>
          </p>
          <p className="mt-5 max-w-[320px] text-[15px] leading-relaxed text-foreground/70">
            A survivor-led sanctuary for nervous system healing, self-reflection,
            education, community, and therapist discovery.
          </p>
        </div>

        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-3 lg:gap-10">
          {/* Explore */}
          <div className="space-y-5">
            <h3 className={headingClass}>Explore</h3>
            <ul className="space-y-3">
              {EXPLORE_LINKS.filter((l) => showJoinCta || (l.to !== "/choose-membership" && l.to !== "/guest")).map((l) => (
                <li key={l.label}>
                  <Link to={l.to!} hash={l.hash} className={linkClass}>
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <InstallAppButton />
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="space-y-5">
            <h3 className={headingClass}>Legal</h3>
            <ul className="space-y-3">
              {LEGAL_LINKS.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className={linkClass}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div className="space-y-5">
            <h3 className={headingClass}>Connect</h3>
            <ul className="flex flex-wrap gap-3">
              {SOCIALS.map((s) => {
                const Icon = s.icon;
                return (
                  <li key={s.label}>
                    <a
                      href={s.href}
                      onClick={(e) => openExternal(s.href, e)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      title={s.label}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 bg-background text-[var(--gold-deep)] shadow-sm transition-all hover:-translate-y-0.5 hover:border-[var(--gold-deep)] hover:bg-gold-light/40"
                    >
                      <Icon className="h-[18px] w-[18px]" />
                    </a>
                  </li>
                );
              })}
            </ul>
            <ul className="space-y-2 pt-2 text-[14px]">
              <li>
                <a href="mailto:info@recoverytrauma.com" className="block font-medium text-[var(--gold-deep)] transition-opacity hover:opacity-80">
                  info@recoverytrauma.com
                </a>
              </li>
              <li>
                <a href="mailto:therapists@recoverytrauma.com" className="block font-medium text-[var(--gold-deep)] transition-opacity hover:opacity-80">
                  therapists@recoverytrauma.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Compact newsletter signup */}
        <div className="mt-12 rounded-2xl border border-gold/25 bg-cream/50 p-5 dark:bg-cream/10">
          <NewsletterSignup source="footer" variant="compact" />
        </div>

        {/* Scope and safety statement — same wording as every other page */}
        <p className="mx-auto mt-12 max-w-3xl text-center text-xs leading-relaxed text-foreground/65">
          Recovery Trauma<span className="text-[var(--gold-deep)]">™</span> provides educational
          resources, self-help tools, peer support and wellbeing content. Recovery Trauma
          <span className="text-[var(--gold-deep)]">™</span>{" "}
          <span className="font-semibold text-foreground">
            is not therapy, medical treatment, or a crisis service
          </span>
          , and is not a substitute for professional healthcare, counselling or emergency support.
          Nothing here diagnoses or treats any condition. If you are in immediate danger or need
          urgent help, contact local emergency services or a{" "}
          <Link to="/crisis" className="font-medium text-[var(--gold-deep)] underline-offset-2 hover:underline">
            crisis line
          </Link>
          .
        </p>

        {/* Bottom strip */}
        <div className="mt-16 flex flex-col items-center justify-between gap-3 border-t border-gold/20 pt-8 sm:flex-row">
          <p className="text-xs text-foreground/55">
            © {year} Recovery Trauma<span className="text-[var(--gold-deep)]">™</span> Ltd · Company No. 16340718
          </p>
          <p className="font-display text-[12px] italic text-[var(--gold-deep)]/85">
            Built with care by survivors, for survivors.
          </p>
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs text-foreground/55">
            Platform built with{" "}
            <a
              href="https://lovable.dev/invite/IN5H0NP"
              target="_blank"
              rel="noopener noreferrer"
              className="underline-offset-2 hover:text-[var(--gold-deep)] hover:underline"
            >
              Lovable
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default LandingFooter;
